import { JSX, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import ReportPreviewDialog, { ReportPreviewDialogProps } from './reportPreviewDialog';

interface ReportPreviewButtonProps extends ReportPreviewDialogProps {
  /**
   * Default state of the preview dialog
   * @default false
   */
  defaultDialogState?: boolean;
  /**
   * Flag indicating if the button should be in loading state
   */
  isLoading: boolean;
  /**
   * Callback for preview button click (to save changes before preview for example)
   */
  onClickPreview: () => Promise<void>;
  /**
   * Label for the button
   * @default 'Preview'
   */
  label?: string;
  /**
   * Keyword param for the preview url.
   * Some sections need this param to determine what doc to preview.
   */
  keyword?: string;
}

/**
 * Render button for previewing report and report preview dialog
 */
const ReportPreviewButton = ({
  isLoading,
  onClickPreview,
  defaultDialogState = false,
  label = 'Preview',
  keyword,
  ...dialogProps
}: ReportPreviewButtonProps): JSX.Element => {
  const [openReviewDialog, setOpenReviewDialog] = useState(defaultDialogState);

  const handleClickPreview = async (): Promise<void> => {
    await onClickPreview();
    setOpenReviewDialog(true);
  };

  return (
    <>
      <LoadingButton loading={isLoading} variant="contained" onClick={handleClickPreview}>
        {label}
      </LoadingButton>
      <ReportPreviewDialog
        keyword={keyword}
        open={openReviewDialog}
        onClose={(): void => setOpenReviewDialog(false)}
        {...dialogProps}
      />
    </>
  );
};

export default ReportPreviewButton;
